// Canned news criteria for news generation and research reports
// This file contains predefined research criteria that BE agents will use to research and generate news stories

export const cannedNewsCriteria = [
  {
    id: "ai-agents-tech-research",
    name: "AI Agents - Tech Research",
    description:
      "Research latest AI agents developments and technical advances for B2B audience",
    criteria: {
      searchSessionId: "ai_agents_research_001",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI Agents latest developments 2024",
        searchCriteria: {
          maxResults: 5,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          excludeDomains: ["spam-site.com", "clickbait-news.com"],
          includeDomains: ["techcrunch.com", "wired.com", "theverge.com"],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "TECHNICAL",
          targetLength: "MEDIUM",
          maxWordCount: 800,
          includeSources: true,
          includeQuotes: true,
          focusAreas: ["technical_advances", "implementation_details"],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.7,
          maxTokens: 2000,
          systemPrompt:
            "You are a professional news analyst specializing in AI and technology trends. Provide clear, concise analysis with actionable insights.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "HIGH",
        tags: ["ai-agents", "research", "news-analysis", "technology"],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "high",
      expectedProcessingTime: "15-20 minutes",
      estimatedValue: 5000,
    },
  },
  {
    id: "competitive-analysis-research",
    name: "Competitive Analysis Research",
    description:
      "Research competitive landscape and market positioning for strategic insights",
    criteria: {
      searchSessionId: "competitive_analysis_research_002",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI Agents competitive landscape",
        searchCriteria: {
          maxResults: 8,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: [
            "openai.com",
            "anthropic.com",
            "google.com",
            "microsoft.com",
          ],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "ANALYTICAL",
          targetLength: "LONG",
          maxWordCount: 1200,
          includeSources: true,
          includeQuotes: true,
          focusAreas: ["competitive_analysis", "market_positioning"],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.6,
          maxTokens: 2500,
          systemPrompt:
            "You are a strategic analyst specializing in competitive intelligence. Provide detailed competitive analysis with market positioning insights.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "HIGH",
        tags: [
          "competitive-analysis",
          "research",
          "market-intelligence",
          "strategy",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "high",
      expectedProcessingTime: "25-30 minutes",
      estimatedValue: 12000,
    },
  },
  {
    id: "market-adoption-research",
    name: "Market Adoption Research",
    description:
      "Research market adoption trends and business impact for strategic planning",
    criteria: {
      searchSessionId: "market_adoption_research_003",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI Agents market adoption trends",
        searchCriteria: {
          maxResults: 6,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          // No includeDomains for broader search
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "PROFESSIONAL",
          targetLength: "MEDIUM",
          maxWordCount: 800,
          includeSources: true,
          includeQuotes: true,
          focusAreas: ["market_trends", "business_impact"],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.7,
          maxTokens: 2000,
          systemPrompt:
            "You are a business analyst specializing in market trends and adoption patterns. Provide insights on market adoption with business impact analysis.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "MEDIUM",
        tags: [
          "market-research",
          "adoption-trends",
          "business-impact",
          "strategy",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "medium",
      expectedProcessingTime: "20-25 minutes",
      estimatedValue: 8000,
    },
  },
  {
    id: "startup-ecosystem-research",
    name: "Startup Ecosystem Research",
    description:
      "Research startup ecosystem trends, funding, and innovation patterns",
    criteria: {
      searchSessionId: "startup_ecosystem_research_004",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI startup ecosystem funding trends 2024",
        searchCriteria: {
          maxResults: 7,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: [
            "techcrunch.com",
            "venturebeat.com",
            "pitchbook.com",
            "crunchbase.com",
          ],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "PROFESSIONAL",
          targetLength: "MEDIUM",
          maxWordCount: 800,
          includeSources: true,
          includeQuotes: true,
          focusAreas: [
            "funding_trends",
            "innovation_patterns",
            "market_opportunities",
          ],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.7,
          maxTokens: 2000,
          systemPrompt:
            "You are a startup ecosystem analyst specializing in funding trends and innovation patterns. Provide insights on startup ecosystem dynamics.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "MEDIUM",
        tags: [
          "startup-ecosystem",
          "funding-trends",
          "innovation",
          "market-analysis",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "medium",
      expectedProcessingTime: "20-25 minutes",
      estimatedValue: 10000,
    },
  },
  {
    id: "industry-insights-daily",
    name: "Industry Insights - Daily Brief",
    description:
      "Generate daily industry insights brief for executive decision making",
    criteria: {
      searchSessionId: "industry_insights_daily_005",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI industry breaking news today",
        searchCriteria: {
          maxResults: 5,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: ["reuters.com", "bloomberg.com", "wsj.com", "ft.com"],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "PROFESSIONAL",
          targetLength: "SHORT",
          maxWordCount: 400,
          includeSources: true,
          includeQuotes: true,
          focusAreas: ["breaking_news", "market_moves", "strategic_insights"],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.6,
          maxTokens: 1500,
          systemPrompt:
            "You are an executive analyst providing concise daily briefings. Focus on actionable insights and strategic implications.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "HIGH",
        tags: [
          "daily-brief",
          "executive-insights",
          "breaking-news",
          "strategic",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "high",
      expectedProcessingTime: "10-15 minutes",
      estimatedValue: 3000,
    },
  },
  {
    id: "technical-deep-dive",
    name: "Technical Deep Dive Research",
    description:
      "In-depth technical analysis of AI implementation patterns and architectures",
    criteria: {
      searchSessionId: "technical_deep_dive_006",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI agents technical implementation patterns architecture",
        searchCriteria: {
          maxResults: 10,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: [
            "github.com",
            "stackoverflow.com",
            "medium.com",
            "dev.to",
            "arxiv.org",
          ],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "TECHNICAL",
          targetLength: "LONG",
          maxWordCount: 1500,
          includeSources: true,
          includeQuotes: true,
          focusAreas: [
            "technical_implementation",
            "architecture_patterns",
            "best_practices",
            "code_examples",
          ],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.5,
          maxTokens: 3000,
          systemPrompt:
            "You are a senior software architect specializing in AI systems. Provide detailed technical analysis with code examples and architectural insights.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "MEDIUM",
        tags: [
          "technical-analysis",
          "architecture",
          "implementation",
          "best-practices",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "medium",
      expectedProcessingTime: "30-35 minutes",
      estimatedValue: 15000,
    },
  },
  {
    id: "enterprise-adoption-research",
    name: "Enterprise Adoption Research",
    description:
      "Research enterprise AI adoption patterns and ROI case studies",
    criteria: {
      searchSessionId: "enterprise_adoption_007",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "enterprise AI agents adoption ROI case studies",
        searchCriteria: {
          maxResults: 8,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: [
            "forrester.com",
            "gartner.com",
            "mckinsey.com",
            "bain.com",
            "deloitte.com",
          ],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "ANALYTICAL",
          targetLength: "MEDIUM",
          maxWordCount: 1000,
          includeSources: true,
          includeQuotes: true,
          focusAreas: [
            "enterprise_adoption",
            "roi_analysis",
            "case_studies",
            "implementation_challenges",
          ],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.6,
          maxTokens: 2500,
          systemPrompt:
            "You are an enterprise technology consultant specializing in AI adoption. Provide data-driven analysis of enterprise AI implementation patterns.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "HIGH",
        tags: [
          "enterprise-adoption",
          "roi-analysis",
          "case-studies",
          "consulting",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "high",
      expectedProcessingTime: "25-30 minutes",
      estimatedValue: 18000,
    },
  },
  {
    id: "regulatory-compliance-research",
    name: "Regulatory Compliance Research",
    description: "Research AI regulatory landscape and compliance requirements",
    criteria: {
      searchSessionId: "regulatory_compliance_008",
      tenantId: "fipsr",
      researchRequest: {
        topicCategory: "NEWS",
        searchQuery: "AI agents regulatory compliance requirements 2024",
        searchCriteria: {
          maxResults: 6,
          timeRange: "last_24_hours",
          language: "en",
          region: "US",
          includeDomains: [
            "federalregister.gov",
            "congress.gov",
            "whitehouse.gov",
            "ftc.gov",
            "sec.gov",
          ],
        },
        reportConfig: {
          deliveryFormat: "WRITTEN",
          tone: "PROFESSIONAL",
          targetLength: "MEDIUM",
          maxWordCount: 800,
          includeSources: true,
          includeQuotes: true,
          focusAreas: [
            "regulatory_requirements",
            "compliance_frameworks",
            "legal_implications",
            "risk_assessment",
          ],
          excludeTopics: ["politics", "celebrity_news"],
        },
        aiConfig: {
          model: "anthropic.claude-3-sonnet-20240229-v1:0",
          temperature: 0.4,
          maxTokens: 2000,
          systemPrompt:
            "You are a legal technology expert specializing in AI regulation. Provide clear analysis of regulatory requirements and compliance implications.",
        },
        serpApiConfig: {
          engine: "google",
          gl: "us",
          hl: "en",
        },
      },
      metadata: {
        requestedBy: "user@fipsr.com",
        priority: "HIGH",
        tags: [
          "regulatory-compliance",
          "legal-analysis",
          "risk-assessment",
          "compliance",
        ],
        expiresAt: "2024-12-31T23:59:59Z",
      },
    },
    metadata: {
      priority: "high",
      expectedProcessingTime: "20-25 minutes",
      estimatedValue: 12000,
    },
  },
];

// Helper function to enhance queue record input with additional metadata
export const enhanceNewsQueueRecordInput = (
  baseInput,
  startupName,
  userMode = "default"
) => {
  const enhancedInput = {
    ...baseInput,
    // Add tenant/entity information
    tenantId: startupName || "default",
    entityType: "NEWSGENMGR", // News generation entity type
    userMode,

    // Add timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),

    // Add processing metadata
    priority: baseInput.metadata?.priority || "medium",
    expectedProcessingTime:
      baseInput.metadata?.expectedProcessingTime || "15-20 minutes",

    // Add source tracking
    source: "sales-process-dashboard",
    sourceScreen: "news-generation-queue",

    // Add business context
    businessContext: {
      startupName,
      userMode,
      reportType: "research-generation",
      expectedOutcome: "research-report",
      entityType: "NEWSGENMGR", // News generation entity type
    },
  };

  return enhancedInput;
};

// Helper function to get canned criteria by ID
export const getCannedNewsCriteriaById = (id) => {
  return cannedNewsCriteria.find((criteria) => criteria.id === id);
};

// Helper function to validate canned news criteria
export const validateCannedNewsCriteria = (criteria) => {
  const requiredFields = ["searchSessionId", "tenantId", "researchRequest"];
  const missingFields = requiredFields.filter((field) => !criteria[field]);

  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
  }

  // Validate researchRequest structure
  const researchRequest = criteria.researchRequest;
  if (
    !researchRequest.searchQuery ||
    !researchRequest.searchCriteria ||
    !researchRequest.reportConfig
  ) {
    throw new Error(
      "Invalid researchRequest structure - missing required sub-fields"
    );
  }

  return true;
};
